/**
 * Assets API client
 */

import { apiClient } from './client';
import type { Asset, AssetCreateData, AssetUpdateData } from '../../types/asset';

export const assetsApi = {
  /**
   * Create a new asset
   */
  async create(companyId: string, data: AssetCreateData): Promise<Asset> {
    // Transform camelCase to snake_case for API
    const payload = {
      asset_type_id: data.assetTypeId,
      field_values: data.fieldValues,
    };
    const response = await apiClient.post<Asset>(
      `/api/v1/assets?company_id=${companyId}`,
      payload
    );
    return response.data;
  },

  /**
   * List assets from a company
   */
  async list(
    companyId: string,
    options?: {
      assetTypeId?: string;
      activeOnly?: boolean;
    }
  ): Promise<Asset[]> {
    const params = new URLSearchParams({ company_id: companyId });
    
    if (options?.assetTypeId) {
      params.append('asset_type_id', options.assetTypeId);
    }
    
    if (options?.activeOnly) {
      params.append('active_only', 'true');
    }
    
    const response = await apiClient.get<Asset[]>(`/api/v1/assets?${params.toString()}`);
    return response.data;
  },

  /**
   * Get asset by ID
   */
  async getById(assetId: string, companyId: string): Promise<Asset> {
    const response = await apiClient.get<Asset>(
      `/api/v1/assets/${assetId}?company_id=${companyId}`
    );
    return response.data;
  },

  /**
   * Update asset
   */
  async update(assetId: string, companyId: string, data: AssetUpdateData): Promise<Asset> {
    // Transform camelCase to snake_case for API
    const payload: any = {};
    if (data.fieldValues !== undefined) payload.field_values = data.fieldValues;
    if (data.isActive !== undefined) payload.is_active = data.isActive;

    const response = await apiClient.put<Asset>(
      `/api/v1/assets/${assetId}?company_id=${companyId}`,
      payload
    );
    return response.data;
  },

  /**
   * Delete asset
   */
  async delete(assetId: string, companyId: string): Promise<void> {
    await apiClient.delete(`/api/v1/assets/${assetId}?company_id=${companyId}`);
  },

  /**
   * Deactivate asset
   */
  async deactivate(assetId: string, companyId: string): Promise<void> {
    await apiClient.patch(`/api/v1/assets/${assetId}/deactivate?company_id=${companyId}`);
  },
};
